// Vote Storage Utility for managing user votes in localStorage

export interface UserVote {
  id: string;
  title: string;
  file: File | null;
  fileName?: string;
  fileType?: string;
  imageUrl?: string;
  videoUrl?: string;
  media_type?: string; // Type of media (image or video)
  category?: string; // Category field for votes
  option1: string;
  option2: string;
  option1Icon: string;
  option2Icon: string;
  status?: 'pending' | 'approved';
  createdAt: number;
  updatedAt: number;
  isPublished?: boolean;
  likeCount?: number;
  votes?: {
    up: number;
    down: number;
    total: number;
  }
  product_image?: File | null;
  product_name?: string;
  product_link?: string;
}

// Internal interface for localStorage storage (with serialized file)
interface SerializedUserVote {
  id: string;
  title: string;
  fileData: string | null; // base64 encoded file data
  fileName?: string;
  fileType?: string;
  option1: string;
  option2: string;
  option1Icon: string;
  option2Icon: string;
  status?: 'pending' | 'approved';
  createdAt: number;
  updatedAt: number;
  product_image?: File | null;
  product_name?: string;
  product_link?: string;
}

export interface VoteStorageData {
  votes: SerializedUserVote[];
}

const STORAGE_KEY = 'user_votes';

// File size limits (in bytes)
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB for images
const MAX_VIDEO_SIZE = 10 * 1024 * 1024; // 10MB for videos

export class VoteStorage {
  // Validate file size before processing
  static validateFileSize(file: File): { isValid: boolean; error?: string } {
    if (!file) return { isValid: true };

    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
    const fileTypeName = isVideo ? 'video' : 'file';
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `${fileTypeName} size exceeds ${maxSizeMB}MB limit. Please choose a smaller file.`
      };
    }

    return { isValid: true };
  }

  // Helper method to convert File to base64 string
  private static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Helper method to convert base64 string back to File
  private static base64ToFile(base64: string, fileName: string, fileType: string): File {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], fileName, { type: fileType });
  }

  // Helper method to serialize UserVote to SerializedUserVote
  private static async serializeVote(vote: UserVote): Promise<SerializedUserVote> {
    const fileData = vote.file ? await this.fileToBase64(vote.file) : null;
    const { file, ...voteWithoutFile } = vote;
    return {
      ...voteWithoutFile,
      fileData,
      fileName: vote.file?.name || vote.fileName,
      fileType: vote.file?.type || vote.fileType,
    };
  }

  // Helper method to deserialize SerializedUserVote to UserVote
  private static deserializeVote(serializedVote: SerializedUserVote): UserVote {
    const file = serializedVote.fileData && serializedVote.fileName && serializedVote.fileType
      ? this.base64ToFile(serializedVote.fileData, serializedVote.fileName, serializedVote.fileType)
      : null;

    const { fileData, ...voteWithoutFileData } = serializedVote;
    return {
      ...voteWithoutFileData,
      file,
    };
  }

  // Get all votes for the current user
  static getAllVotes(): UserVote[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return [];

      const parsed: VoteStorageData = JSON.parse(data);
      const serializedVotes = parsed.votes || [];
      return serializedVotes.map(vote => this.deserializeVote(vote));
    } catch (error) {
      console.error('Error reading votes from localStorage:', error);
      return [];
    }
  }

  // Save a new vote
  static async saveVote(vote: Omit<UserVote, 'id' | 'createdAt' | 'updatedAt'>): Promise<UserVote> {
    // Validate file size before saving
    if (vote.file) {
      const validation = this.validateFileSize(vote.file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
    }

    const votes = this.getAllVotes();
    const newVote: UserVote = {
      ...vote,
      id: this.generateId(),
      status: vote.status || 'pending',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    console.log(votes, "votesvotes")
    votes.push(newVote);
    await this.saveVotes(votes);
    return newVote;
  }

  // Update an existing vote
  static async updateVote(id: string, updates: Partial<Omit<UserVote, 'id' | 'createdAt'>>): Promise<UserVote | null> {
    // Validate file size if updating with a new file
    if (updates.file) {
      const validation = this.validateFileSize(updates.file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
    }

    const votes = this.getAllVotes();
    const index = votes.findIndex(vote => vote.id === id);

    if (index === -1) return null;

    const updatedVote: UserVote = {
      ...votes[index],
      ...updates,
      updatedAt: Date.now(),
    };

    votes[index] = updatedVote;
    await this.saveVotes(votes);
    return updatedVote;
  }

  // Delete a vote
  static async deleteVote(id: string): Promise<boolean> {
    const votes = this.getAllVotes();
    const filteredVotes = votes.filter(vote => vote.id !== id);

    if (filteredVotes.length === votes.length) return false;

    await this.saveVotes(filteredVotes);
    return true;
  }

  // Get a specific vote by ID
  static getVoteById(id: string): UserVote | null {
    const votes = this.getAllVotes();
    return votes.find(vote => vote.id === id) || null;
  }

  // Private helper methods
  private static async saveVotes(votes: UserVote[]): Promise<void> {
    try {
      const serializedVotes = await Promise.all(
        votes.map(vote => this.serializeVote(vote))
      );
      const data: VoteStorageData = { votes: serializedVotes };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving votes to localStorage:', error);

      // Handle specific quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        throw new Error('Storage quota exceeded. Please try uploading a smaller file or delete some existing votes.');
      }

      // Handle other storage errors
      if (error instanceof Error) {
        throw new Error(`Failed to save vote: ${error.message}`);
      }

      throw new Error('Failed to save vote due to an unknown error.');
    }
  }

  private static generateId(): string {
    return `vote_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Clear all votes (for testing or reset purposes)
  static clearAllVotes(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  // Get votes count
  static getVotesCount(): number {
    return this.getAllVotes().length;
  }

  // Search votes by title
  static searchVotes(searchTerm: string): UserVote[] {
    const votes = this.getAllVotes();
    const term = searchTerm.toLowerCase();

    return votes.filter(vote =>
      vote.title.toLowerCase().includes(term) ||
      vote.option1.toLowerCase().includes(term) ||
      vote.option2.toLowerCase().includes(term)
    );
  }

  // Sort votes by creation date (newest first)
  static getSortedVotes(ascending: boolean = false): UserVote[] {
    const votes = this.getAllVotes();
    return votes.sort((a, b) =>
      ascending ? a.createdAt - b.createdAt : b.createdAt - a.createdAt
    );
  }
}